/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { mcpEndpointsCheckSlugAvailability } from "../funcs/mcpEndpointsCheckSlugAvailability.js";
import { mcpEndpointsCreate } from "../funcs/mcpEndpointsCreate.js";
import { mcpEndpointsDelete } from "../funcs/mcpEndpointsDelete.js";
import { mcpEndpointsGet } from "../funcs/mcpEndpointsGet.js";
import { mcpEndpointsList } from "../funcs/mcpEndpointsList.js";
import { mcpEndpointsUpdate } from "../funcs/mcpEndpointsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { ListMcpEndpointsResult } from "../models/components/listmcpendpointsresult.js";
import { McpEndpoint } from "../models/components/mcpendpoint.js";
import {
  CheckMcpEndpointSlugAvailabilityRequest,
  CheckMcpEndpointSlugAvailabilitySecurity,
} from "../models/operations/checkmcpendpointslugavailability.js";
import {
  CreateMcpEndpointRequest,
  CreateMcpEndpointSecurity,
} from "../models/operations/createmcpendpoint.js";
import {
  DeleteMcpEndpointRequest,
  DeleteMcpEndpointSecurity,
} from "../models/operations/deletemcpendpoint.js";
import {
  GetMcpEndpointRequest,
  GetMcpEndpointSecurity,
} from "../models/operations/getmcpendpoint.js";
import {
  ListMcpEndpointsRequest,
  ListMcpEndpointsSecurity,
} from "../models/operations/listmcpendpoints.js";
import {
  UpdateMcpEndpointRequest,
  UpdateMcpEndpointSecurity,
} from "../models/operations/updatemcpendpoint.js";
import { unwrapAsync } from "../types/fp.js";

export class McpEndpoints extends ClientSDK {
  /**
   * checkMcpEndpointSlugAvailability mcpEndpoints
   *
   * @remarks
   * Check whether an MCP endpoint slug is available. The uniqueness scope depends on whether a custom_domain_id is provided: platform-domain slugs are checked across all platform-domain endpoints (custom_domain_id IS NULL); custom-domain slugs are checked within the (custom_domain_id, slug) pair. Returns true when the slug is free.
   */
  async checkSlugAvailability(
    request: CheckMcpEndpointSlugAvailabilityRequest,
    security?: CheckMcpEndpointSlugAvailabilitySecurity | undefined,
    options?: RequestOptions,
  ): Promise<boolean> {
    return unwrapAsync(mcpEndpointsCheckSlugAvailability(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createMcpEndpoint mcpEndpoints
   *
   * @remarks
   * Create a new MCP endpoint for an MCP server or a meta MCP server. Provide exactly one of mcp_server_id or meta_mcp_server_id.
   */
  async create(
    request: CreateMcpEndpointRequest,
    security?: CreateMcpEndpointSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpEndpoint> {
    return unwrapAsync(mcpEndpointsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteMcpEndpoint mcpEndpoints
   *
   * @remarks
   * Delete an MCP endpoint
   */
  async delete(
    request: DeleteMcpEndpointRequest,
    security?: DeleteMcpEndpointSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(mcpEndpointsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMcpEndpoint mcpEndpoints
   *
   * @remarks
   * Get an MCP endpoint by id or by (custom_domain_id, slug). Provide either id, or slug with an optional custom_domain_id — not both.
   */
  async get(
    request?: GetMcpEndpointRequest | undefined,
    security?: GetMcpEndpointSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpEndpoint> {
    return unwrapAsync(mcpEndpointsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMcpEndpoints mcpEndpoints
   *
   * @remarks
   * List MCP endpoints for a project. Optionally filter to only those associated with a specific MCP server or meta MCP server (not both).
   */
  async list(
    request?: ListMcpEndpointsRequest | undefined,
    security?: ListMcpEndpointsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMcpEndpointsResult> {
    return unwrapAsync(mcpEndpointsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateMcpEndpoint mcpEndpoints
   *
   * @remarks
   * Update an MCP endpoint. This is a full-record replace: fields omitted from the request become null on the stored record. The id and slug fields are required, along with exactly one of mcp_server_id or meta_mcp_server_id.
   */
  async update(
    request: UpdateMcpEndpointRequest,
    security?: UpdateMcpEndpointSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpEndpoint> {
    return unwrapAsync(mcpEndpointsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
